import {
  LockOutlined,
  UserOutlined,
  GoogleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Layout,
  notification,
  Typography,
  Divider,
  Space,
} from "antd";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { login } from "../../../service";
import { googleLoginApi, AuthResponseData } from "../../../service/authApi";
import { secureCookieOptions } from "../../../service/utils";
import Cookies from "js-cookie";

const { Title, Text, Link } = Typography;

interface LoginFormProps {
  refetch: () => void;
  onSwitchToRegister: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  refetch,
  onSwitchToRegister,
}) => {
  const { mutateAsync, isLoading } = useMutation(login, {
    onSuccess: async (data) => {
      if (data.access && data.refresh) {
        Cookies.set("accessToken", data.access, secureCookieOptions);
        Cookies.set("refreshToken", data.refresh, secureCookieOptions);
        refetch();
        notification.success({
          message: `Witaj`,
          style: {
            background: "#52c41a30",
          },
        });
      } else {
        notification.error({
          message: "Błąd logowania",
          style: {
            background: "#ff4d4f30",
          },
        });
      }
    },
    onError: () => {
      notification.error({
        message: "Błąd logowania",
        style: {
          background: "#ff4d4f30",
        },
      });
    },
  });

  const [formData, setFormData] = useState({ username: "", password: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = () => {
    mutateAsync(formData);
  };

  const handleGoogleLogin = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      notification.error({
        message: "Google Login nie skonfigurowany",
        description:
          "Brak REACT_APP_GOOGLE_CLIENT_ID w zmiennych środowiskowych",
      });
      return;
    }

    try {
      // @ts-ignore — Google Identity Services global
      const google = window.google;
      if (!google?.accounts?.id) {
        notification.error({
          message: "Google SDK nie załadowany",
          description: "Odśwież stronę i spróbuj ponownie",
        });
        return;
      }

      google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          try {
            const data: AuthResponseData = await googleLoginApi(
              response.credential
            );
            Cookies.set("accessToken", data.accessToken, secureCookieOptions);
            Cookies.set("refreshToken", data.refreshToken, secureCookieOptions);
            notification.success({
              message: "Zalogowano przez Google!",
              style: { background: "#52c41a30" },
            });
            refetch();
          } catch (err: any) {
            notification.error({
              message: "Błąd logowania Google",
              description: err?.message || "Spróbuj ponownie",
              style: { background: "#ff4d4f30" },
            });
          }
        },
      });

      google.accounts.id.prompt();
    } catch {
      notification.error({
        message: "Błąd Google Login",
        description: "Spróbuj ponownie",
      });
    }
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      <div
        style={{
          width: 380,
          padding: 32,
          borderRadius: 12,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", marginBottom: 24 }}
        >
          <Title level={3} style={{ textAlign: "center", margin: 0 }}>
            Zaloguj się
          </Title>
          <Text
            type="secondary"
            style={{ textAlign: "center", display: "block" }}
          >
            Flex Management
          </Text>
        </Space>

        <Button
          icon={<GoogleOutlined />}
          size="large"
          block
          onClick={handleGoogleLogin}
          style={{
            marginBottom: 16,
            height: 44,
            borderRadius: 8,
            fontWeight: 500,
          }}
        >
          Zaloguj przez Google
        </Button>

        <Divider plain>
          <Text type="secondary" style={{ fontSize: 12 }}>
            lub zaloguj się hasłem
          </Text>
        </Divider>

        <Form onFinish={handleSubmit} size="large">
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: "Proszę wprowadzić nazwę użytkownika!",
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Nazwa użytkownika"
              name="username"
              onChange={handleInputChange}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Proszę wprowadzić hasło!" }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Hasło"
              name="password"
              onChange={handleInputChange}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              loading={isLoading}
              htmlType="submit"
              block
              style={{
                height: 44,
                borderRadius: 8,
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              Zaloguj się
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: "center" }}>
          <Text type="secondary">Nie masz konta? </Text>
          <Link onClick={onSwitchToRegister}>Zarejestruj się</Link>
        </div>
      </div>
    </Layout>
  );
};
